import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { PageHeader } from "@/components/AppLayout";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck, ShieldAlert, RefreshCw, ChevronLeft, ChevronRight, Globe, Activity, History } from "lucide-react";
import { Navigate } from "react-router-dom";

const ACTION_COLORS = {
  "auth.login.ok": "border-emerald-300 bg-emerald-50 text-emerald-800",
  "auth.login.fail": "border-rose-300 bg-rose-50 text-rose-800",
  "auth.logout": "border-slate-300 bg-slate-50 text-slate-700",
  "auth.register": "border-violet-300 bg-violet-50 text-violet-800",
  "auth.password_change": "border-amber-300 bg-amber-50 text-amber-800",
  "create": "border-cyan-300 bg-cyan-50 text-cyan-800",
  "update": "border-sky-300 bg-sky-50 text-sky-800",
  "delete": "border-rose-300 bg-rose-50 text-rose-800",
  "integration.connect": "border-violet-300 bg-violet-50 text-violet-800",
  "integration.disconnect": "border-slate-300 bg-slate-50 text-slate-700",
  "email.send": "border-amber-300 bg-amber-50 text-amber-800",
  "quote.accept": "border-emerald-300 bg-emerald-50 text-emerald-800",
};

const METHOD_COLORS = {
  GET: "border-slate-300 bg-slate-50 text-slate-700",
  POST: "border-cyan-300 bg-cyan-50 text-cyan-800",
  PATCH: "border-sky-300 bg-sky-50 text-sky-800",
  DELETE: "border-rose-300 bg-rose-50 text-rose-800",
};

const LIMIT = 50;

function fmt(ts) {
  if (!ts) return "—";
  return new Date(ts).toLocaleString("en-AU", { day: "2-digit", month: "short", year: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
}

function flagFor(code) {
  if (!code || code.length !== 2) return "";
  return String.fromCodePoint(...[...code.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)));
}

function MutationsTab() {
  const [action, setAction] = useState("all");
  const [targetType, setTargetType] = useState("");
  const [userEmailFilter, setUserEmailFilter] = useState("");
  const [page, setPage] = useState(0);

  const { data: actionsList = [] } = useQuery({
    queryKey: ["audit-actions"],
    queryFn: async () => (await api.get("/audit/actions")).data,
  });

  const params = new URLSearchParams();
  if (action && action !== "all") params.set("action", action);
  if (targetType) params.set("target_type", targetType);
  params.set("limit", String(LIMIT));
  params.set("skip", String(page * LIMIT));

  const { data, refetch } = useQuery({
    queryKey: ["audit-logs", action, targetType, page],
    queryFn: async () => (await api.get(`/audit/logs?${params.toString()}`)).data,
  });

  const logs = (data?.logs || []).filter((l) =>
    !userEmailFilter || (l.user_email || "").toLowerCase().includes(userEmailFilter.toLowerCase())
  );
  const total = data?.total ?? 0;
  const pageStart = page * LIMIT + 1;
  const pageEnd = Math.min((page + 1) * LIMIT, total);

  return (
    <>
      <Card className="rounded-sm p-4 industrial-shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <div className="stamp-label text-slate-600 mb-1.5">Action</div>
            <Select value={action} onValueChange={(v) => { setAction(v); setPage(0); }}>
              <SelectTrigger data-testid="audit-action-filter" className="rounded-sm h-10"><SelectValue placeholder="All actions" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All actions</SelectItem>
                {actionsList.map((a) => <SelectItem key={a.action} value={a.action}>{a.action} ({a.count})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="stamp-label text-slate-600 mb-1.5">Target type</div>
            <Input data-testid="audit-target-filter" placeholder="deal, quote, user…" value={targetType} onChange={(e) => { setTargetType(e.target.value); setPage(0); }} className="rounded-sm h-10" />
          </div>
          <div>
            <div className="stamp-label text-slate-600 mb-1.5">User email contains</div>
            <Input data-testid="audit-user-filter" value={userEmailFilter} onChange={(e) => setUserEmailFilter(e.target.value)} className="rounded-sm h-10" />
          </div>
          <div className="flex items-end justify-between gap-2">
            <div className="text-xs text-slate-500 stamp-label">{total > 0 ? `${pageStart}–${pageEnd} / ${total}` : "—"}</div>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="rounded-sm h-10"><RefreshCw size={13} /></Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm industrial-shadow overflow-hidden mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left stamp-label text-slate-600">
                <th className="px-3 py-2.5 w-44">When</th>
                <th className="px-3 py-2.5 w-48">User</th>
                <th className="px-3 py-2.5 w-32">IP</th>
                <th className="px-3 py-2.5 w-44">Action</th>
                <th className="px-3 py-2.5 w-24">Target</th>
                <th className="px-3 py-2.5">Details</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="audit-table-body">
              {logs.map((l) => (
                <tr key={l.id} className="hover:bg-cyan-50/30" data-testid={`audit-row-${l.id}`}>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-700 whitespace-nowrap">{fmt(l.ts)}</td>
                  <td className="px-3 py-2">
                    <div className="text-slate-900 text-xs font-medium truncate">{l.user_email || "—"}</div>
                    {l.user_role && <div className="text-[10px] text-slate-500">{l.user_role}</div>}
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{l.ip || "—"}</td>
                  <td className="px-3 py-2">
                    <Badge variant="outline" className={`rounded-sm text-[10px] ${ACTION_COLORS[l.action] || "border-slate-300 bg-slate-50 text-slate-700"}`}>{l.action}</Badge>
                    {l.status === "fail" && <span className="ml-1.5 text-[10px] text-rose-600 font-semibold">FAIL</span>}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    <div className="text-slate-700">{l.target_type || "—"}</div>
                    {l.target_id && <div className="font-mono text-[9px] text-slate-400 truncate max-w-[100px]" title={l.target_id}>{l.target_id.slice(0, 8)}…</div>}
                  </td>
                  <td className="px-3 py-2">
                    {l.meta && Object.keys(l.meta).length > 0
                      ? <pre className="font-mono text-[10px] text-slate-600 max-w-md overflow-x-auto bg-slate-50 px-2 py-1 rounded-sm whitespace-pre-wrap break-words">{JSON.stringify(l.meta)}</pre>
                      : <span className="text-slate-400 text-xs">—</span>}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={6} className="px-3 py-12 text-center text-slate-400 text-sm">No audit entries match the filters</td></tr>}
            </tbody>
          </table>
        </div>
        {total > LIMIT && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-500 stamp-label">Page {page + 1} of {Math.ceil(total / LIMIT)}</div>
            <div className="flex gap-1.5">
              <Button data-testid="audit-prev-page" variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded-sm h-8 px-2"><ChevronLeft size={14} /></Button>
              <Button data-testid="audit-next-page" variant="outline" size="sm" disabled={pageEnd >= total} onClick={() => setPage((p) => p + 1)} className="rounded-sm h-8 px-2"><ChevronRight size={14} /></Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

function AccessLogTab() {
  const [method, setMethod] = useState("all");
  const [pathLike, setPathLike] = useState("");
  const [ipFilter, setIpFilter] = useState("");
  const [page, setPage] = useState(0);

  const params = new URLSearchParams();
  if (method && method !== "all") params.set("method", method);
  if (pathLike) params.set("path_like", pathLike);
  if (ipFilter) params.set("ip", ipFilter);
  params.set("limit", String(LIMIT));
  params.set("skip", String(page * LIMIT));

  const { data, refetch } = useQuery({
    queryKey: ["access-logs", method, pathLike, ipFilter, page],
    queryFn: async () => (await api.get(`/audit/access-logs?${params.toString()}`)).data,
  });
  const logs = data?.logs || [];
  const total = data?.total ?? 0;

  return (
    <>
      <Card className="rounded-sm p-4 industrial-shadow">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <div className="stamp-label text-slate-600 mb-1.5">Method</div>
            <Select value={method} onValueChange={(v) => { setMethod(v); setPage(0); }}>
              <SelectTrigger data-testid="access-method-filter" className="rounded-sm h-10"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="GET">GET</SelectItem>
                <SelectItem value="POST">POST</SelectItem>
                <SelectItem value="PATCH">PATCH</SelectItem>
                <SelectItem value="DELETE">DELETE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="stamp-label text-slate-600 mb-1.5">Path contains</div>
            <Input data-testid="access-path-filter" placeholder="/api/deals" value={pathLike} onChange={(e) => { setPathLike(e.target.value); setPage(0); }} className="rounded-sm h-10" />
          </div>
          <div>
            <div className="stamp-label text-slate-600 mb-1.5">IP exact</div>
            <Input data-testid="access-ip-filter" placeholder="34.16.56.64" value={ipFilter} onChange={(e) => { setIpFilter(e.target.value); setPage(0); }} className="rounded-sm h-10" />
          </div>
          <div className="flex items-end justify-between gap-2">
            <div className="text-xs text-slate-500 stamp-label">{total} total</div>
            <Button variant="outline" size="sm" onClick={() => refetch()} className="rounded-sm h-10"><RefreshCw size={13} /></Button>
          </div>
        </div>
      </Card>

      <Card className="rounded-sm industrial-shadow overflow-hidden mt-4">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left stamp-label text-slate-600">
                <th className="px-3 py-2.5 w-44">When</th>
                <th className="px-3 py-2.5 w-16">Mtd</th>
                <th className="px-3 py-2.5">Path</th>
                <th className="px-3 py-2.5 w-14">Status</th>
                <th className="px-3 py-2.5 w-16">ms</th>
                <th className="px-3 py-2.5 w-32">IP</th>
                <th className="px-3 py-2.5 w-44">User</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-testid="access-table-body">
              {logs.map((l, i) => (
                <tr key={`${l.ts}-${l.path}-${i}`} className="hover:bg-cyan-50/30">
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-700 whitespace-nowrap">{fmt(l.ts)}</td>
                  <td className="px-3 py-2"><Badge variant="outline" className={`rounded-sm text-[10px] ${METHOD_COLORS[l.method] || ""}`}>{l.method}</Badge></td>
                  <td className="px-3 py-2 font-mono text-[11px] text-slate-700 truncate max-w-md" title={l.path + (l.query ? "?" + l.query : "")}>{l.path}{l.query ? <span className="text-slate-400">?{l.query.slice(0, 40)}{l.query.length > 40 ? "…" : ""}</span> : null}</td>
                  <td className="px-3 py-2 font-mono text-[11px]">
                    <span className={l.status >= 500 ? "text-rose-600" : l.status >= 400 ? "text-amber-600" : "text-emerald-600"}>{l.status}</span>
                  </td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{l.ms}</td>
                  <td className="px-3 py-2 font-mono text-[10px] text-slate-500">{l.ip}</td>
                  <td className="px-3 py-2 text-xs text-slate-700 truncate">{l.user_email || <span className="text-slate-400">(anon)</span>}</td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={7} className="px-3 py-12 text-center text-slate-400 text-sm">No access log entries</td></tr>}
            </tbody>
          </table>
        </div>
        {total > LIMIT && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50">
            <div className="text-xs text-slate-500 stamp-label">Page {page + 1} of {Math.ceil(total / LIMIT)}</div>
            <div className="flex gap-1.5">
              <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="rounded-sm h-8 px-2"><ChevronLeft size={14} /></Button>
              <Button variant="outline" size="sm" disabled={(page + 1) * LIMIT >= total} onClick={() => setPage((p) => p + 1)} className="rounded-sm h-8 px-2"><ChevronRight size={14} /></Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
}

function IpMapTab() {
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["audit-ip-summary"],
    queryFn: async () => (await api.get("/audit/ip-summary")).data,
    staleTime: 60000,
  });

  const ips = data?.ips || [];

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs text-slate-500 stamp-label">
          Source: <span className="font-mono">{data?.source || "—"}</span> · {ips.length} unique IPs · geolocation cached
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="rounded-sm h-9" data-testid="ip-refresh-btn">
          <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
          <span className="ml-1.5">Refresh</span>
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ips.map((row) => (
          <Card key={row.ip} className="rounded-sm industrial-shadow overflow-hidden" data-testid={`ip-card-${row.ip}`}>
            <div className="p-4 flex items-start gap-4">
              <div className="w-12 h-12 bg-cyan-50 border border-cyan-200 flex items-center justify-center font-display text-2xl shrink-0">
                {flagFor(row.country_code) || <Globe className="text-slate-400" size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline gap-2 flex-wrap">
                  <span className="font-mono font-semibold text-slate-900">{row.ip}</span>
                  <Badge variant="outline" className="rounded-sm text-[10px] border-cyan-300 bg-cyan-50 text-cyan-800">{row.count} req</Badge>
                </div>
                <div className="text-sm text-slate-700 mt-1 font-medium">
                  {row.city || "—"}{row.city && row.country ? ", " : ""}{row.country || ""}
                </div>
                <div className="text-xs text-slate-500 mt-0.5 truncate" title={row.org}>{row.org || "(no org info)"}</div>
                <div className="text-[10px] text-slate-400 mt-2 stamp-label">Last seen: {fmt(row.last_seen)}</div>
                {row.users.length > 0 && (
                  <div className="text-[10px] text-slate-500 mt-1.5 font-mono truncate" title={row.users.join(", ")}>
                    Users: {row.users.slice(0, 3).join(", ")}{row.users.length > 3 ? ` +${row.users.length - 3}` : ""}
                  </div>
                )}
                {row.lat != null && row.lon != null && (
                  <a
                    href={`https://www.openstreetmap.org/?mlat=${row.lat}&mlon=${row.lon}#map=10/${row.lat}/${row.lon}`}
                    target="_blank" rel="noreferrer"
                    className="text-[10px] text-cyan-600 hover:text-cyan-700 mt-1.5 inline-block underline"
                  >
                    View on map ({row.lat.toFixed(2)}, {row.lon.toFixed(2)})
                  </a>
                )}
              </div>
            </div>
          </Card>
        ))}
        {ips.length === 0 && (
          <Card className="rounded-sm p-12 industrial-shadow col-span-2 text-center text-slate-400 text-sm">
            No IPs recorded yet — make any API call to populate.
          </Card>
        )}
      </div>
    </>
  );
}

export default function AuditLog() {
  const { user } = useAuth();
  const [tab, setTab] = useState("mutations");

  const { data: verify } = useQuery({
    queryKey: ["audit-verify"],
    queryFn: async () => (await api.get("/audit/verify")).data,
    enabled: user?.role === "admin",
    refetchOnWindowFocus: false,
  });

  const { data: stats } = useQuery({
    queryKey: ["audit-stats"],
    queryFn: async () => (await api.get("/audit/stats")).data,
    enabled: user?.role === "admin",
  });

  const { data: accessSum } = useQuery({
    queryKey: ["access-summary"],
    queryFn: async () => (await api.get("/audit/access-summary")).data,
    enabled: user?.role === "admin",
  });

  if (user?.role !== "admin") return <Navigate to="/dashboard" replace />;

  const TABS = [
    { key: "mutations", label: "Mutations", icon: History, count: stats?.total_30d },
    { key: "access", label: "Access Log", icon: Activity, count: accessSum?.total_30d },
    { key: "ips", label: "IP Map", icon: Globe, count: accessSum?.by_ip?.length },
  ];

  return (
    <div>
      <PageHeader
        title="Audit Log"
        subtitle="Tamper-evident mutations, full request log, and source-IP geolocation."
        breadcrumb={["System", "Audit Log"]}
      />

      <div className="px-6 lg:px-10 py-6 space-y-4">
        {/* Status cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card className={`rounded-sm p-4 border-l-4 ${verify?.ok ? "border-l-emerald-500" : "border-l-rose-500"} industrial-shadow`} data-testid="audit-chain-card">
            <div className="flex items-center gap-3">
              {verify?.ok ? <ShieldCheck className="text-emerald-600" size={24} /> : <ShieldAlert className="text-rose-600" size={24} />}
              <div>
                <div className="stamp-label text-slate-500">Hash chain</div>
                <div className="font-display font-semibold text-lg">
                  {verify ? (verify.ok ? `Intact (${verify.total} rows)` : `Broken @ ${verify.broken_at}`) : "—"}
                </div>
              </div>
            </div>
          </Card>
          <Card className="rounded-sm p-4 industrial-shadow">
            <div className="stamp-label text-slate-500">Mutations (30d)</div>
            <div className="font-display font-bold text-2xl mt-0.5">{stats?.total_30d ?? "—"}</div>
            <div className="text-xs text-slate-500">tamper-evident chain</div>
          </Card>
          <Card className="rounded-sm p-4 industrial-shadow">
            <div className="stamp-label text-slate-500">All requests (30d)</div>
            <div className="font-display font-bold text-2xl mt-0.5">{accessSum?.total_30d ?? "—"}</div>
            <div className="text-xs text-slate-500">across {accessSum?.by_ip?.length ?? 0} IPs</div>
          </Card>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          {TABS.map((t) => {
            const Icon = t.icon;
            const isActive = tab === t.key;
            return (
              <button
                key={t.key}
                data-testid={`audit-tab-${t.key}`}
                onClick={() => setTab(t.key)}
                className={`px-4 h-10 flex items-center gap-2 text-sm font-semibold border-b-2 transition-colors ${
                  isActive ? "border-cyan-600 text-cyan-700" : "border-transparent text-slate-500 hover:text-slate-900"
                }`}
              >
                <Icon size={14} /> {t.label}
                {t.count != null && (
                  <span className="text-[10px] font-mono text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded-sm">{t.count}</span>
                )}
              </button>
            );
          })}
        </div>

        {tab === "mutations" && <MutationsTab />}
        {tab === "access" && <AccessLogTab />}
        {tab === "ips" && <IpMapTab />}
      </div>
    </div>
  );
}
